// Story 7.1 (ADO-904), AC1/AC2/AC6 — the product listing for a selected node
// (Figma "Category Listing"). Renders the breadcrumb, the filters rail, the
// product grid, and — for an empty node — an explicit empty state (AC6), never
// an error. Epic 7 pixel parity (ADO-903): geometry, toolbar, pagination footer
// and mobile filters drawer ported from the prototype category-listing page.

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banner,
  Button,
  CollapsibleFilterSidebar,
  Icon,
  Pagination,
  Toggle,
} from '../../design-system';
import { FiltersDrawer } from './FiltersDrawer';
import { useViewMode } from './useViewMode';
import {
  type CategoryNode,
  fetchChildren,
  fetchNodeProducts,
  hasProductScope,
  NodeNotFoundError,
  NO_PRODUCT_SCOPE,
  type NodeListing,
  type NodeListingPage,
  type ProductScope,
  type ProductSummary,
} from '../../api/browse.client';
import {
  hasActiveFilters,
  filtersKeyOf,
  sameFilters,
  type CatalogueNarrowing,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { useFilterPanel } from '../../api/useFilterPanel';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useMultibuyDeals } from '../../multibuy/useMultibuyDealsStore';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import type { FavouriteList } from '../../api/favourites.client';
import { useCatalogueAccountCode, useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import {
  resolvesFeaturedLines,
  useCatalogueReadAccountCode,
} from '../../ordering/catalogueReadAccount';
import { formatCategoryName } from './categoryDisplay';
import { Breadcrumb, type BreadcrumbLink } from './Breadcrumb';
import { FilterChips, type DrillChip } from './FilterChips';
import { FilterPanel } from './FilterPanel';
import { removeCoupledFilterValue, toggleCoupledFilterValue } from './rbpFilterCoupling';
import { ProductCard } from './ProductCard';
import { BROWSE_PAGE_SIZE_OPTIONS, DEFAULT_BROWSE_PAGE_SIZE } from './browsePagination';
import styles from './CategoryListing.module.css';

export interface CategoryListingProps {
  /** The selected node whose products to list; null = the ROOT (all products). */
  readonly node: CategoryNode | null;
  /** Navigate to a breadcrumb crumb (`null` = Shop root, clears the selection). */
  readonly onNavigate: (node: CategoryNode | null) => void;
  /** Epic 7 demo polish (2026-07-17) — the prototype's `?view=favourites` mode:
   *  the SAME listing shell (rail, toolbar, grid/list toggle, cards) but the
   *  product source is the favourites store (no catalogue fetch), the breadcrumb
   *  becomes a "Favourites" heading + "View all" link, chips/pagination hide,
   *  and the empty state is the prototype's heart prompt. */
  readonly favouritesView?: boolean;
  /** Story 7.29 (ADO-2025, D-7.29-35): "View all" and the empty state's "Browse all products"
   *  leave the favourites view for the catalogue; the rail's Department and Category narrow the
   *  favourites through `onNavigate` instead. Defaults to `onNavigate(null)`. */
  readonly onViewAll?: () => void;
  /**
   * Story 8.1 (ADO-2010), Tasks 8.2/8.3/8.4 — the landing-tile scope from the URL (a
   * tile's categories, an LSN category, Catering only). It ANDs with the node and the
   * facets, and each part is a removable chip (A10.4: the Catering default can be
   * dropped to see every product at the depot). Ignored in favourites view.
   */
  readonly scope?: ProductScope;
  /** The name a hierarchy scope's chip shows (the landing tile's name). */
  readonly scopeLabel?: string | undefined;
  /** Replace the scope (the host owns it, in the URL). */
  readonly onScopeChange?: (next: ProductScope) => void;
  /**
   * Story 7.31 (ADO-1113), AC1/AC3 — the collected-offers union mode, held by the HOST in the URL
   * (`/products?offers=all`). It asks the server for every live, scoped, priced and orderable offer
   * on the active account, one row per on-offer product, whatever its kind. It ANDs with the node,
   * the facets and the scope, and when the union is genuinely empty the listing shows a truthful
   * "nothing on offer" state rather than a generic empty node (a transport failure stays an error).
   */
  readonly collectedOffers?: boolean;
  /**
   * Story 7.31 (ADO-1113): the rail's "On Offer" checkbox and its chip turn the union on or off.
   * A host that holds the union in its URL (`/products`) is told of the change and writes it;
   * without one (the plan view) the listing holds the union itself, starting from
   * `collectedOffers`.
   */
  readonly onCollectedOffersChange?: (on: boolean) => void;
  /** Offer the "Catering only" switch: Foodservice accounts only (A10.4). */
  readonly offerCateringOnly?: boolean;
  /**
   * Story 7.33 (ADO-2026): content the host shows above the grid, under the toolbar (the plan
   * view's Format and Category pickers and the plan image, Figma 6603:99108).
   */
  readonly header?: ReactNode;
  /**
   * Story 7.33 (ADO-2026): a control beside the Filters button (the phone's "Planogram" chip,
   * Figma 7054:165663, where the header has no room for it). The host renders it only on the
   * layout that draws it, so the control is never in the page twice.
   */
  readonly toolbarAction?: ReactNode;
  /**
   * Story 7.33 (ADO-2026): the breadcrumb's links ahead of its root and the root's own name, for
   * a listing that is a collection of its own (the plan view's "Shop > Planogram > {plan}").
   */
  readonly breadcrumbLeadingLinks?: readonly BreadcrumbLink[] | undefined;
  readonly breadcrumbRootLabel?: string | undefined;
  /**
   * Story 7.33 (ADO-2026): the empty state's words when the listing is a collection of its own
   * (a plan's products) rather than a category, and nothing in it is in the range.
   */
  readonly emptyCopy?: { readonly title: string; readonly hint: string };
  /**
   * Story 7.33 (ADO-2026, D-7.33-K): the words on the not-found state's way back, where that is
   * not the catalogue root (the plan view's goes back to the whole plan).
   */
  readonly notFoundBackLabel?: string;
  /**
   * Story 7.35 (ADO-2032) — URL-controlled mode. When the host (`/products`)
   * drives the listing from the address bar it passes `urlControlled` and the
   * controlled inputs below; the component then reads its request intent (node,
   * facets, page, page size) from props and reports user actions back through the
   * callbacks rather than owning that state internally. Omitted, the component
   * keeps its self-contained state (the standalone render used by existing specs).
   */
  readonly urlControlled?: boolean;
  /** The selected node UUID from the URL (`null` = the ROOT listing). Takes precedence over
   *  `node` for the request. Story 7.33 (ADO-2026, D-7.33-K): the plan view passes it without
   *  `urlControlled`, so its node comes from the URL while its facets and page stay in the
   *  component; omitted (`undefined`), the request follows `node`. */
  readonly nodeId?: string | null | undefined;
  /** The active attribute facets from the URL (controlled mode). */
  readonly filters?: ProductFilters;
  /** Apply a new facet selection (controlled mode writes it to the URL). */
  readonly onFiltersChange?: (filters: ProductFilters) => void;
  /** Story 7.29 (ADO-2025, D-7.29-37): replace the facet selection with what a read applied,
   *  once it answered the selection on screen (controlled mode writes it to the URL with a
   *  replace and keeps the page, so the prune neither adds a Back step nor moves the page). */
  readonly onFiltersPrune?: (applied: ProductFilters) => void;
  /** The 1-based browse page from the URL (controlled mode). */
  readonly page?: number;
  /** The applied page size from the URL (controlled mode). */
  readonly pageSize?: number;
  /** Navigate to a page at the committed size (controlled mode writes both to the
   *  URL, so a size whose load failed is never applied behind the shopper's back). */
  readonly onPageChange?: (page: number, pageSize: number) => void;
  /** Change the page size (controlled mode writes it to the URL). */
  readonly onPageSizeChange?: (pageSize: number) => void;
  /** Clear facets + scope + the taxonomy drill as one atomic URL change. */
  readonly onClearAll?: () => void;
  /** A listing resolved: hand back its breadcrumb (to restore the drill), its
   *  committed page/size (so the host can canonicalise a server page-clamp) and the
   *  request it answers (so the host can tell whether its URL still names this
   *  request, or the URL has moved on). */
  readonly onResolved?: (resolved: {
    readonly breadcrumb: readonly CategoryNode[];
    readonly page: number;
    readonly pageSize: number;
    readonly requested: ListingRequest;
  }) => void;
  /** The named node does not exist (a stale deep link) — the host may clear the
   *  optimistic selection; the component also renders an explicit not-found state. */
  readonly onNotFound?: () => void;
}

/** What one listing fetch asked for: the request a resolved listing answers. */
export interface ListingRequest {
  readonly nodeId: string | null;
  readonly filters: ProductFilters;
  readonly scope: ProductScope;
  readonly collectedOffers: boolean;
  readonly page: number;
  readonly pageSize: number;
}

/** A stable empty-facets reference for controlled mode (effect-dependency safe). */
const EMPTY_FILTERS: ProductFilters = Object.freeze({});

type Phase = 'idle' | 'loading' | 'error' | 'ready' | 'notFound';

// Story 7-FU-D (ADO-1217), AC3 — the first visual row of cards is the LCP
// candidate, so mark the first N as `priority` (eager + fetchpriority=high). N is
// a responsive-grid judgement call (D-3): a small constant ≈ one row on the
// widest layout; over-eagering a few extra cards is harmless, under-eagering
// defeats the LCP fix. True LCP timing is a Playwright/manual concern.
const EAGER_IMAGE_COUNT = 4;

/**
 * What a loaded listing said about narrowing (Story 7.29, ADO-2025): the narrowings the rail
 * renders, and the selection the server applied (D-7.29-29), which the chips, the active-filter
 * count and the empty state read. `appliedFilters` and `cateringOnly` are null when the response
 * did not carry them (an older server), so the raw selection stands in. The echo stands only for
 * the selection the listing answered (`answeredFiltersKey`, `answeredCateringOnly`).
 */
interface KnownNarrowings {
  readonly narrowings: readonly CatalogueNarrowing[];
  readonly unavailableNarrowings: readonly CatalogueNarrowing[];
  readonly appliedFilters: ProductFilters | null;
  readonly cateringOnly: boolean | null;
  readonly answeredFiltersKey: string;
  readonly answeredCateringOnly: boolean;
}

/** No narrowings: before any listing has loaded, the rail offers nothing. */
const NO_NARROWINGS: KnownNarrowings = {
  narrowings: [],
  unavailableNarrowings: [],
  appliedFilters: null,
  cateringOnly: null,
  answeredFiltersKey: '',
  answeredCateringOnly: false,
};

/** `scope` without one of its parts (a removed chip, or a Catering only the server ignored). */
function withoutScopePart(scope: ProductScope, key: keyof ProductScope): ProductScope {
  const next: { -readonly [K in keyof ProductScope]: ProductScope[K] } = { ...scope };
  delete next[key];
  return next;
}

/**
 * Enabler E7-C (ADO-2274), D-1: the listing is one ordering entry's range, so its state (grid,
 * page, filters, departments, a page error) belongs to that entry. The body is keyed by the entry
 * and starts afresh whenever it changes, rather than each piece of state being reset by hand.
 * With no entry it waits in its loading state and asks nothing.
 */
export function CategoryListing(props: CategoryListingProps) {
  const catalogueEntryKey = useCatalogueEntryKey();
  return (
    <CategoryListingForEntry
      key={catalogueEntryKey ?? 'no-entry'}
      {...props}
      catalogueEntryKey={catalogueEntryKey}
    />
  );
}

function CategoryListingForEntry({
  node,
  onNavigate,
  favouritesView = false,
  onViewAll,
  scope = NO_PRODUCT_SCOPE,
  scopeLabel,
  onScopeChange,
  collectedOffers: collectedOffersProp = false,
  onCollectedOffersChange,
  offerCateringOnly = false,
  header,
  toolbarAction,
  breadcrumbLeadingLinks,
  breadcrumbRootLabel,
  emptyCopy,
  notFoundBackLabel = 'Back to catalogue root',
  catalogueEntryKey,
  urlControlled = false,
  nodeId: controlledNodeId,
  filters: controlledFilters,
  onFiltersChange,
  onFiltersPrune,
  page: controlledPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
  onClearAll,
  onResolved,
  onNotFound,
}: CategoryListingProps & { readonly catalogueEntryKey: string | null }) {
  // Story 7.31 (ADO-1113): the collected-offers union the "On Offer" checkbox drives, the host's
  // where it holds it, else the listing's own.
  const [ownCollectedOffers, setOwnCollectedOffers] = useState(collectedOffersProp);
  const collectedOffers =
    onCollectedOffersChange !== undefined ? collectedOffersProp : ownCollectedOffers;
  const setCollectedOffers = (on: boolean) => {
    if (onCollectedOffersChange !== undefined) onCollectedOffersChange(on);
    else setOwnCollectedOffers(on);
  };
  // Story 7.10 (ADO-906): the active attribute filters (AC1/AC3). Reset when the
  // node changes (scoped to their node). `showFilters` toggles the pinned rail on
  // desktop (Filters Switch, Figma 531:1752); `drawerOpen` drives the <1024px
  // filters drawer (prototype mobile Drawer).
  const [filters, setFilters] = useState<ProductFilters>({});
  const { showFilters, toggleFilters } = useFilterPanel();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Story 7.29 (ADO-2025), D-7.29-21: the active account rides the listing for the narrowing gates.
  const catalogueAccountCode = useCatalogueAccountCode();
  // The last successfully loaded listing for the current node/page — the anchor
  // for the breadcrumb, node header, facet panel, and the pagination envelope.
  // Retained across a page/filter refetch so the current grid stays on screen
  // while the next page is in flight (no blank flash on Previous/Next).
  const [listing, setListing] = useState<NodeListing | null>(null);
  // Story 7.29 (ADO-2025): the narrowings of the last listing that loaded, null before any has.
  // The rail renders these, so it keeps its options while the next read is in flight or after it
  // failed, rather than emptying to its header. They belong to the entry and the active account
  // (the account-type gates read the account, D-7.29-21): the body is keyed by the entry, and an
  // account switch within the entry drops them at render time, below, so another account's
  // options are never shown. A node switch keeps them, so the previous node's unavailable list
  // shows briefly while the next node loads (D-7.29-28).
  const [knownNarrowings, setKnownNarrowings] = useState<KnownNarrowings | null>(null);
  const [narrowingsAccountCode, setNarrowingsAccountCode] = useState(catalogueAccountCode);
  if (catalogueAccountCode !== narrowingsAccountCode) {
    setNarrowingsAccountCode(catalogueAccountCode);
    setKnownNarrowings(null);
  }
  // Story 7-FU-C (ADO-1216), AC2 — the products of the CURRENT page (each page
  // REPLACES the previous, offset pagination — not the earlier accumulate-on-Next
  // load-more), plus the pagination envelope (`page`/`pageSize`/`total`/`totalPages`).
  const [products, setProducts] = useState<readonly ProductSummary[]>([]);
  const [page, setPage] = useState<NodeListingPage | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // Story 7-FU-C (ADO-1216), AC2 — the SINGLE request intent: the page AND the
  // "Per page" size the next fetch will use, plus a `nonce` so a retry of the SAME
  // page/size (its nonce bumped) still re-fires the fetch effect. Page size lives
  // HERE (not in a separate `pageSize` state) on purpose: what the footer DISPLAYS
  // (indicator, "Per page" highlight, Previous/Next enablement) is read from the
  // committed `page` envelope below — never this request-side intent — and folding
  // size into the intent means a failed "Per page" change can't strand a size that
  // the footer, a later Previous/Next, or a later filter change would then apply
  // behind the user's back (PR #252 review, r3). Selecting a size, changing a
  // filter, and paging all write a fresh intent; the committed envelope only moves
  // on a successful load.
  const [fetchReq, setFetchReq] = useState<{ page: number; pageSize: number; nonce: number }>({
    page: 1,
    pageSize: DEFAULT_BROWSE_PAGE_SIZE,
    nonce: 0,
  });
  // A transient page-navigation failure: the current grid stays put and a retry
  // notice appears, so a failed Previous/Next never blanks what the user is viewing.
  const [pageError, setPageError] = useState(false);
  // Monotonic fetch generation. A settlement whose generation is no longer current
  // (a newer node/filter/page fetch started) is stale and dropped — it must never
  // paint results onto the wrong node/page.
  const fetchSeqRef = useRef(0);
  // Mirrors `listing` for the fetch effect's catch, WITHOUT making the effect
  // depend on `listing` (which would refetch on every settlement). Lets a failed
  // fetch tell a FIRST load (nothing on screen → error state) from a page
  // NAVIGATION (grid present → keep it + show the retry notice).
  const listingRef = useRef<NodeListing | null>(listing);
  listingRef.current = listing;
  // Story 7.35 (ADO-2032): hold the latest host callbacks in refs so the fetch
  // effect can call them without listing them as dependencies — an inline
  // callback from the host would otherwise re-fire the effect (a refetch loop) on
  // every host render.
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onNotFoundRef = useRef(onNotFound);
  onNotFoundRef.current = onNotFound;
  const onFiltersPruneRef = useRef(onFiltersPrune);
  onFiltersPruneRef.current = onFiltersPrune;

  // The applied "Per page" size — read from the committed envelope (moves only on a
  // successful load), never the request-side intent. The single source for the
  // footer highlight AND for the size a page/filter change keeps, so a failed size
  // change can't leave the two disagreeing (PR #252 review).
  const committedPageSize = page?.pageSize ?? DEFAULT_BROWSE_PAGE_SIZE;

  // Request a page at the CURRENTLY APPLIED size: bump the nonce so even a same-page
  // request re-fires the effect, and clear any prior page error. Previous/Next and
  // the over-range recovery route through here; a size change goes through
  // `selectPageSize`, a filter/node change through the reset block below.
  const requestPage = (target: number) => {
    setPageError(false);
    if (urlControlled) {
      // Story 7.35 (ADO-2032): the page lives in the URL — hand it to the host,
      // which writes it and re-drives the fetch through the `page` prop. It pages
      // at the COMMITTED size, like the uncontrolled path (PR #252 review): after a
      // failed "Per page" change the URL still holds the size that never loaded,
      // and paging at it would skip rows. When the URL already names that page at
      // that size (its load failed), writing it again would change nothing, so
      // re-request it instead.
      const nextPage = Math.max(1, target);
      if (nextPage === reqPage && committedPageSize === reqPageSize) {
        setRetryNonce((n) => n + 1);
      } else onPageChange?.(nextPage, committedPageSize);
      return;
    }
    setFetchReq((r) => ({
      page: Math.max(1, target),
      pageSize: committedPageSize,
      nonce: r.nonce + 1,
    }));
  };
  // Retry the LAST request verbatim (same page AND size, nonce bumped) — the retry
  // notice's "Try again" re-attempts exactly what failed, whether that was a
  // Previous/Next, a "Per page" change, or a filter change.
  const retryFetch = () => {
    setPageError(false);
    if (urlControlled) {
      // Story 7.35 (ADO-2032): controlled mode's request intent lives in the URL
      // (props), so a retry can't bump a request-side nonce — it bumps a
      // dedicated retry counter the fetch effect also depends on.
      setRetryNonce((n) => n + 1);
      return;
    }
    setFetchReq((r) => ({ ...r, nonce: r.nonce + 1 }));
  };
  // Story 7.35 (ADO-2032): in controlled mode the page/size/filters intent is
  // owned by the host (the URL), so a "Try again" re-fires the fetch effect
  // through this counter instead of the request-side `fetchReq` nonce.
  const [retryNonce, setRetryNonce] = useState(0);
  // Epic 7 re-skin (ADO-903): grid/list view toggle, opening in the grid view at
  // every width (shared with ProductSearch).
  const { viewMode, selectView } = useViewMode();
  // The full Department list + the current Department's categories power the
  // sidebar Department/Category sections (real taxonomy navigation). Fetched
  // lazily via the same hierarchy client the mega-menu uses.
  const [departments, setDepartments] = useState<readonly CategoryNode[]>([]);
  const [categories, setCategories] = useState<readonly CategoryNode[]>([]);

  // Story 7.35 (ADO-2032) — the effective request intent. In URL-controlled mode
  // the node, facets, page and page size come from props (the address bar); in
  // self-contained mode they come from internal state as before. A `nodeId` given
  // without `urlControlled` (Story 7.33's plan view, D-7.33-K) is the node from the
  // URL, with the facets and page still internal.
  const effectiveNodeId =
    urlControlled || controlledNodeId !== undefined
      ? (controlledNodeId ?? null)
      : (node?.id ?? null);
  const activeFilters = urlControlled ? (controlledFilters ?? EMPTY_FILTERS) : filters;
  const reqPage = urlControlled ? (controlledPage ?? 1) : fetchReq.page;
  const reqPageSize = urlControlled
    ? (controlledPageSize ?? DEFAULT_BROWSE_PAGE_SIZE)
    : fetchReq.pageSize;
  const reqNonce = urlControlled ? retryNonce : fetchReq.nonce;
  const nodeId = effectiveNodeId;

  // Reset filter state synchronously when the selected node changes — filters are
  // scoped to their node and must not leak across a Department/Category switch.
  // Doing it during render (not in an effect) means the fetch effect below never
  // fires a transient request with the previous node's stale filters (PR #74 review).
  const [renderedNodeId, setRenderedNodeId] = useState(nodeId);
  if (nodeId !== renderedNodeId) {
    setRenderedNodeId(nodeId);
    // Story 7.35 (ADO-2032): in controlled mode the facets are owned by the host
    // (dropped from the URL on a node change), so the component must not reset its
    // internal filters; it still blanks the grid so nothing leaks across nodes.
    if (!urlControlled) setFilters({});
    setListing(null);
    // Story 7-FU-C (ADO-1216), AC2 — a node switch blanks the grid + pagination
    // so nothing leaks across nodes, and starts a fresh page 1 (the explicit
    // `setFetchReq` below fires the fetch even when `filters` are unchanged).
    setProducts([]);
    setPage(null);
    setPageError(false);
    // A node switch always starts at page 1 and re-fires the fetch — even when the
    // new node's filters (hence `resetKey`) are unchanged (e.g. ROOT → a node both
    // with no filters), so the `resetKey` block below would not fire. The applied
    // "Per page" size carries across the switch (a user preference, not node state).
    // In controlled mode the page/size intent is the URL's, so the fetch effect
    // re-fires from its prop dependencies instead of a request-side bump.
    if (!urlControlled) {
      setFetchReq((r) => ({ page: 1, pageSize: committedPageSize, nonce: r.nonce + 1 }));
    }
    // Epic 7 demo polish (2026-07-17): `null` is the ROOT (all-products) listing
    // now — a real fetch, not an idle prompt.
    setPhase('loading');
  }

  // Story 7-FU-C (ADO-1216), AC2 — a FILTER change RESETS to page 1 (an offset into
  // a now-different result set is meaningless), keeping the applied "Per page" size.
  // Keyed on the filters alone — a page-SIZE change is handled by `selectPageSize`
  // (which writes its own intent), so it must NOT ride this key: keying on a
  // request-side size would let a failed size change strand the key and silently
  // refetch a later filter change at the size that never loaded (PR #252 review, r3).
  // The reset keeps `committedPageSize` (the last SUCCESSFUL size), so the applied
  // size always matches the footer. `fetchReq`'s bumped nonce re-fires the fetch
  // effect. Multiple intermediate resets in one commit collapse to a single effect
  // run with the final filters (effects run after commit, not per render pass).
  // Story 7.35 (ADO-2032): controlled mode drives page resets through the URL (the
  // host drops `page` on a facet/scope change), so this internal reset is
  // self-contained mode only.
  // Story 7.29 (ADO-2025, D-7.29-37): keyed on what the selection selects, not on how its object
  // was built, so the same selection re-seeded from the URL in another order is not a change.
  const filtersKey = filtersKeyOf(activeFilters);
  // Story 8.1 (ADO-2010): a scope change is a filter change too (page 1, same size).
  // Story 7.31 (ADO-1113): so is entering or leaving the collected-offers union.
  const resetKeyFor = (selection: ProductFilters) =>
    `${filtersKeyOf(selection)}|${JSON.stringify(scope)}|${collectedOffers}`;
  const resetKey = resetKeyFor(filters);
  const [renderedResetKey, setRenderedResetKey] = useState(resetKey);
  if (!urlControlled && resetKey !== renderedResetKey) {
    setRenderedResetKey(resetKey);
    setPageError(false);
    setFetchReq((r) => ({ page: 1, pageSize: committedPageSize, nonce: r.nonce + 1 }));
  }

  // Story 7-FU-C (ADO-1216), AC2 — the page fetch. Each settled `fetchReq`
  // (a node/filter/pageSize reset, a Previous/Next, or a retry) loads exactly that
  // page and REPLACES the grid. `fetchSeqRef` makes any in-flight fetch stale once
  // a newer one starts, so a slow page can never paint over a fresher one.
  // Favourites view — the product source is the store, not the catalogue fetch. Story 17.1
  // (ADO-3722, ADR-024): the tile's favourites come grouped by named list, the default list
  // first; `favourites` holds each product once, for the count and the per-SKU reads.
  const favourites = useFavouritesStore((s) => s.favourites);
  const favouriteLists = useFavouritesStore((s) => s.lists);
  const favouritesHydrated = useFavouritesStore((s) => s.hydrated);
  const favouritesError = useFavouritesStore((s) => s.error);
  const hydrateFavourites = useFavouritesStore((s) => s.hydrate);
  useEffect(() => {
    if (favouritesView) void hydrateFavourites();
  }, [favouritesView, hydrateFavourites]);

  // Story 7.29 (ADO-2025, D-7.29-35): the favourites view reads the catalogue listing scoped to
  // the caller's own favourites (supersedes D-7.29-28), so every narrowing, count, echo and page
  // works on it as on any listing. A heart added or removed after the list has loaded re-reads it
  // (`favouritesRevision`); the list's first hydration does not, so opening the view reads once.
  const favouritesKey = favouritesView
    ? favourites
        .map((favourite) => favourite.sku)
        .sort()
        .join(',')
    : '';
  // The re-read waits until the heart's change has reached the server (no favourite pending), so
  // it never reads the list from before the change.
  const favouritesPending = useFavouritesStore((s) => s.pending.size > 0);
  const [favouritesRevision, setFavouritesRevision] = useState(0);
  const hydratedFavouritesKeyRef = useRef<string | null>(null);

  // Story 7.32 (ADO-1115) and Story 7.29 (ADO-2025): the trading account the listing names, by the
  // rule the search shares (`catalogueReadAccount.ts`): the cart's account where Featured Lines are
  // resolved, the active account elsewhere, none for staff.
  const listingAccountCode = useCatalogueReadAccountCode(
    resolvesFeaturedLines(collectedOffers, activeFilters),
  );

  useEffect(() => {
    if (!favouritesView || !favouritesHydrated || favouritesPending) return;
    const previous = hydratedFavouritesKeyRef.current;
    hydratedFavouritesKeyRef.current = favouritesKey;
    if (previous !== null && previous !== favouritesKey) setFavouritesRevision((n) => n + 1);
  }, [favouritesView, favouritesHydrated, favouritesPending, favouritesKey]);
  const requestScope = useMemo(
    () => (favouritesView ? { ...scope, favourites: true } : scope),
    [favouritesView, scope],
  );

  // The latest selection, readable SYNCHRONOUSLY (Story 7.30): the single-select facets call
  // `onToggleValue` twice in one handler (drop the old value, add the new one, FilterPanel), so
  // the second call must see the first one's result, which the render-time `activeFilters` would
  // not. A settling read also compares it with the selection it asked for (D-7.29-37).
  const latestFiltersRef = useRef(activeFilters);
  latestFiltersRef.current = activeFilters;

  // Story 7.29 (ADO-2025, D-7.29-37): the request in flight or answered last, or a prune recorded
  // as already answered; null after a failure. A run of the effect that would ask the same again
  // (only an object's identity changed, as when a pruned selection is re-seeded from the URL) asks
  // nothing, and a read in flight for it still lands. A retry, a page, a node, a scope, an entry,
  // an account or a favourites change is a different request.
  const lastRequestKeyRef = useRef<string | null>(null);
  // Story 7.35 (ADO-2032): the request the grid on screen answers, keyed on the page and size
  // the server SERVED. When the host canonicalises a server page-clamp into the URL (`page=99`
  // → `page=5`), the new request is the one already on screen, so it is not fetched again.
  const servedRequestRef = useRef<string | null>(null);
  // Story 7.35 (ADO-2032): an unmount (a committed search swaps the listing out) cancels a
  // settlement, so a late response never calls the host's `onResolved` / `onNotFound` for a
  // view the user has left. A superseding run is dropped by `fetchSeqRef`; a run that asks the
  // same again (D-7.29-37) starts nothing and lets the read in flight land.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    const requestKeyFor = (
      selection: ProductFilters,
      requestPage: number,
      requestPageSize: number,
    ): string =>
      JSON.stringify([
        nodeId,
        filtersKeyOf(selection),
        reqNonce,
        requestPage,
        requestPageSize,
        requestScope,
        collectedOffers,
        catalogueEntryKey,
        listingAccountCode,
        favouritesRevision,
      ]);
    const requestKey = requestKeyFor(activeFilters, reqPage, reqPageSize);
    if (urlControlled && listingRef.current !== null && servedRequestRef.current === requestKey) {
      // Supersede any run still in flight: the grid already answers this request, so a
      // retry notice left by a failed request in between no longer applies.
      ++fetchSeqRef.current;
      lastRequestKeyRef.current = requestKey;
      setPhase('ready');
      setPageError(false);
      return;
    }
    if (catalogueEntryKey !== null && requestKey === lastRequestKeyRef.current) return;
    const seq = ++fetchSeqRef.current;
    setPhase('loading');
    // With no entry there is nothing to ask: the page waits (CategoryListing keys this body by the
    // entry, so it starts empty and a chosen entry mounts a fresh one).
    if (catalogueEntryKey === null) {
      lastRequestKeyRef.current = null;
      return;
    }
    lastRequestKeyRef.current = requestKey;
    const requested: ListingRequest = {
      nodeId,
      filters: activeFilters,
      scope,
      collectedOffers,
      page: reqPage,
      pageSize: reqPageSize,
    };
    fetchNodeProducts(
      catalogueEntryKey,
      nodeId,
      undefined,
      activeFilters,
      reqPage,
      reqPageSize,
      requestScope,
      collectedOffers,
      listingAccountCode,
    )
      .then((next) => {
        if (unmountedRef.current || seq !== fetchSeqRef.current) return;
        // D-7.29-37: a read that answered the current selection prunes it, and the URL, to what
        // it applied, so a member the server ignored is neither held nor sent again (the leaflet
        // destination is in the echo whenever it applied). The server ignored the dropped
        // members, so this answer already is the pruned selection's: it is recorded as answered
        // and as the page's reset key, so the prune neither reads again nor moves the page. The
        // URL is written with a replace.
        const applied = next.appliedFilters;
        const prune =
          applied !== undefined &&
          sameFilters(latestFiltersRef.current, activeFilters) &&
          !sameFilters(applied, activeFilters);
        const answeredFilters = prune ? applied : activeFilters;
        servedRequestRef.current = requestKeyFor(
          answeredFilters,
          next.page.page,
          next.page.pageSize,
        );
        lastRequestKeyRef.current = requestKeyFor(answeredFilters, reqPage, reqPageSize);
        setListing(next);
        setKnownNarrowings({
          narrowings: next.narrowings ?? [],
          unavailableNarrowings: next.unavailableNarrowings ?? [],
          appliedFilters: applied ?? null,
          cateringOnly: next.cateringOnly ?? null,
          answeredFiltersKey: filtersKeyOf(answeredFilters),
          answeredCateringOnly: scope.cateringOnly === true,
        });
        setProducts(next.products);
        // Commit the loaded page envelope — the single source of truth for the
        // footer (indicator, "Per page" highlight, Previous/Next). The server
        // echoes the requested page; a failed navigation leaves this untouched, so
        // the footer keeps matching the grid still on screen.
        setPage(next.page);
        setPageError(false);
        setPhase('ready');
        // Story 7.35 (ADO-2032): hand the resolved breadcrumb + committed page/size
        // back to the host so it can restore the drill and canonicalise a server
        // page-clamp into the URL, with the request this read asked for. It runs before
        // the prune's URL write, while the URL still names that request.
        onResolvedRef.current?.({
          breadcrumb: next.breadcrumb,
          page: next.page.page,
          pageSize: next.page.pageSize,
          requested,
        });
        if (prune) {
          latestFiltersRef.current = applied;
          if (urlControlled) {
            onFiltersPruneRef.current?.(applied);
          } else {
            setRenderedResetKey(resetKeyFor(applied));
            setFilters(applied);
          }
        }
      })
      .catch((err: unknown) => {
        if (unmountedRef.current || seq !== fetchSeqRef.current) return;
        lastRequestKeyRef.current = null;
        // Story 7.35 (ADO-2032): a named node the server does not have is an
        // explicit not-found state (a stale deep link), never a retained-grid
        // retry or a generic error — regardless of whether a grid was on screen.
        if (err instanceof NodeNotFoundError) {
          setListing(null);
          setProducts([]);
          setPage(null);
          setPageError(false);
          setPhase('notFound');
          onNotFoundRef.current?.();
          return;
        }
        if (listingRef.current === null) {
          // FIRST load with nothing on screen — surface a clean error state.
          setListing(null);
          setProducts([]);
          setPage(null);
          setPhase('error');
        } else {
          // A page NAVIGATION failed — keep the current grid (and the committed
          // `page` envelope that drives the footer, left untouched) and show a
          // retry notice; Previous/Next/Try-again re-request from here.
          setPhase('ready');
          setPageError(true);
        }
      });
    // A newer effect run bumps `fetchSeqRef`, which supersedes this settlement.
  }, [
    urlControlled,
    nodeId,
    activeFilters,
    reqPage,
    reqPageSize,
    reqNonce,
    scope,
    requestScope,
    collectedOffers,
    catalogueEntryKey,
    listingAccountCode,
    favouritesRevision,
  ]);

  // The products actually on screen: the current page. In the favourites view a heart removed
  // drops its card at once, before the re-read lands, and each card takes its AC4b
  // `accountTypeEligible` from the favourites list (Story 7.5).
  const favouriteBySku = new Map(
    favourites.map((favourite) => [favourite.sku.toLowerCase(), favourite]),
  );
  const displayProducts =
    favouritesView && favouritesHydrated
      ? products.filter((product) => favouriteBySku.has(product.sku.toLowerCase()))
      : products;
  // The favourites view is settling while a read is in flight, or while the page on screen holds a
  // favourite the list has since dropped: its re-read has not landed yet, and may not have started.
  const favouritesSettling =
    favouritesView && (phase === 'loading' || displayProducts.length < products.length);

  // Story 7-FU-C (ADO-1216), AC2 — derived pagination state for the footer. Every
  // value the footer DISPLAYS is anchored to the committed `page` envelope (set
  // only on a successful load), never the request-side `fetchReq` intent. So a
  // failed filter/"Per page" change (grid held on screen, PR #252 review) can't
  // leave the indicator, the "Per page" highlight, or Previous/Next reporting a
  // page/size that never loaded — they stay in step with the grid on screen.
  const totalPages = page?.totalPages ?? 1;
  const total = page?.total ?? 0;
  const committedPage = page?.page ?? 1;
  // `committedPageSize` is derived once, near the request helpers above (the single
  // source shared by the footer highlight and the size a page/filter change keeps).
  // A page navigation is in flight while the grid stays on screen — disable the
  // pager so a double-click can't stack requests or skip a page.
  const navigating = phase === 'loading' && listing !== null;
  // Story 7-FU-C review (PR #252) — an empty window while `total > 0`: an offset
  // past the data. The backend now clamps an over-range page to the LAST page, so
  // this is only reached when the set shrank between the `count` and the row read
  // (a data race) — a recoverable state, NOT an error and NOT the `total === 0`
  // empty state. Only meaningful once a page has settled (`!navigating`); mid-flight
  // the previous page's grid is still held on screen.
  const overRange =
    !favouritesView && listing !== null && total > 0 && displayProducts.length === 0 && !navigating;
  const viewAll = onViewAll ?? (() => onNavigate(null));
  // Story 7-FU-C review (PR #252) — the "Showing X–Y of Z" caption is anchored to
  // the COMMITTED page envelope (`page`, set only on a successful load), never the
  // request-side `fetchReq` intent which changes the instant a Previous/Next/
  // "Per page" is requested. During a navigation the grid still shows the previous page, so
  // anchoring here keeps the caption consistent with what's on screen (no transient
  // "Showing 1–20" over a page-3 grid) and — because the range uses the real
  // on-screen count — it can never invert (the old `total>0 && length===0` case).
  const rangeStart = page !== null ? (page.page - 1) * page.pageSize + 1 : 0;
  const rangeEnd = page !== null ? (page.page - 1) * page.pageSize + displayProducts.length : 0;
  const canPrev = committedPage > 1 && !navigating;
  const canNext = committedPage < totalPages && !navigating;
  const goPrev = () => {
    if (!canPrev) return;
    requestPage(committedPage - 1);
  };
  const goNext = () => {
    if (!canNext) return;
    requestPage(committedPage + 1);
  };
  // Per-page: request page 1 at the new size. The guard compares against the
  // COMMITTED (applied) size, not the request-side intent — so re-selecting the
  // size the footer shows active is a true no-op, while re-selecting a size whose
  // fetch just FAILED (committed size unchanged) correctly retries it rather than
  // being swallowed as "no change" (PR #252 review, r3).
  const selectPageSize = (n: number) => {
    // Story 7.35 (ADO-2032): in controlled mode a failed change leaves its size in
    // the URL, so re-picking the committed size is a no-op only when the URL holds
    // it too; otherwise it must correct the URL. Self-contained mode never strands
    // a size (its pager uses the committed one), so the active size stays a no-op.
    if (n === committedPageSize && (!urlControlled || n === reqPageSize)) return;
    setPageError(false);
    if (urlControlled) {
      // Story 7.35 (ADO-2032): the applied size lives in the URL — the host writes
      // it (and drops `page`, resetting to page 1) and re-drives the fetch. A size
      // the URL already holds failed to load, so re-request it rather than
      // writing an identical URL.
      if (n === reqPageSize && reqPage === 1) setRetryNonce((r) => r + 1);
      else onPageSizeChange?.(n);
      return;
    }
    setFetchReq((r) => ({ page: 1, pageSize: n, nonce: r.nonce + 1 }));
  };

  // Story 7.3 (ADO-1139): populate the availability store (stock pill +
  // "{n} in store") for the products on screen; no-op when unconfigured.
  useAvailability(displayProducts.map((p) => p.sku));
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(displayProducts.map((p) => p.sku));
  // Pricing migration 32 (ADO-3985): the same products' multi-buy deals, one request (D-5: no-op
  // unless VITE_REAL_PRICES_ENABLED is on).
  useMultibuyDeals(displayProducts.map((p) => p.sku));
  // Story 8.12 (ADO-2021): which of them the Customer already has on order, one batched read.
  useOnOrder(displayProducts.map((p) => p.sku));

  // Epic 7 re-skin (ADO-903) — the Department/Category the current listing sits
  // under (breadcrumb is root-first: Department → Category → Sub-Category).
  const currentDepartment = listing?.breadcrumb.find((n) => n.level === 'DEPARTMENT') ?? null;
  const currentCategory = listing?.breadcrumb.find((n) => n.level === 'CATEGORY') ?? null;
  const currentDepartmentId = currentDepartment?.id ?? null;

  // Load the Department list once — the sidebar Department radio. A failure just
  // leaves the section unrendered (the mega-menu remains the primary nav).
  useEffect(() => {
    if (catalogueEntryKey === null) return;
    let cancelled = false;
    fetchChildren(catalogueEntryKey, null)
      .then((res) => {
        if (!cancelled) setDepartments(res.children);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [catalogueEntryKey]);

  // Load the current Department's categories for the sidebar Category section.
  useEffect(() => {
    if (currentDepartmentId === null || catalogueEntryKey === null) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    fetchChildren(catalogueEntryKey, currentDepartmentId)
      .then((res) => {
        if (!cancelled) setCategories(res.children);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentDepartmentId, catalogueEntryKey]);

  // A refetch (e.g. a filter change) keeps the loaded grid on screen; the spinner
  // only shows on the initial load of a node, when there's nothing to hold (PR #74).
  // Story 7.29 (ADO-2025), D-7.29-29: the chips, the active-filter count and the empty state read
  // the selection the last listing applied, so they never claim a narrowing the server ignored
  // (D-7.29-10): a member the range or account is not offered, or a Catering only the account is
  // not offered. The echo stands only for the selection the listing answered: before the first
  // response, after an account switch, and while the selection differs from the one the last
  // listing answered (a toggle or a chip removal in flight, or a read that failed), the raw
  // selection shows, so a chip never lags the rail and removing one always changes the selection.
  const answered = knownNarrowings;
  const appliedFilters =
    answered?.appliedFilters != null && answered.answeredFiltersKey === filtersKey
      ? answered.appliedFilters
      : activeFilters;
  const cateringApplies =
    answered === null ||
    answered.answeredCateringOnly !== (scope.cateringOnly === true) ||
    (answered.cateringOnly ?? answered.narrowings.includes('catering'));
  const appliedScope: ProductScope =
    scope.cateringOnly === true && !cateringApplies
      ? withoutScopePart(scope, 'cateringOnly')
      : scope;
  const scopeActive = hasProductScope(appliedScope);
  const filtersActive = hasActiveFilters(appliedFilters) || scopeActive;
  const facets = listing?.facets ?? [];
  // Story 7.29 (ADO-2025): the catalogue narrowings the listing offers; the rail renders these.
  const { narrowings, unavailableNarrowings } = knownNarrowings ?? NO_NARROWINGS;
  // D-7.29-30: before any listing has loaded (or when the first read failed), Department and
  // Category render from the hierarchy read, so there is always a way out; only a loaded listing
  // can withhold them (ELITE offers no narrowing).
  const railNarrowings: readonly CatalogueNarrowing[] =
    knownNarrowings === null ? ['category'] : narrowings;

  // Story 7.10 — attribute-filter handlers (all produce a NEW filters object →
  // the fetch effect refetches). Clearing restores the unfiltered listing (AC4).
  // Story 7.35 (ADO-2032): in controlled mode each handler produces the next
  // facet set from the URL's and hands it to the host to write, rather than
  // mutating internal state. Both modes build on the LATEST selection
  // (`latestFiltersRef`, above), read synchronously.
  const applyFilters = (next: ProductFilters) => {
    latestFiltersRef.current = next;
    if (urlControlled) onFiltersChange?.(next);
    else setFilters(next);
  };
  const toggleValue = (key: FacetKey, value: string) =>
    applyFilters(toggleCoupledFilterValue(latestFiltersRef.current, key, value));
  // Clear resets the attribute filters AND the taxonomy drill, restoring the
  // unfiltered ROOT (all-products) listing — with no default Department, the
  // Department/Category selections read as filters and "Clear" resets them too
  // (2026-07-17 demo feedback; matches the prototype's Clear).
  const clearNarrowing = () => {
    if (urlControlled) {
      // Story 7.35 (ADO-2032): facets + scope + drill clear as one atomic URL
      // change (one history entry), so the three can't race through separate
      // writes off the same stale query string.
      onClearAll?.();
      return;
    }
    applyFilters({});
    if (scopeActive) onScopeChange?.(NO_PRODUCT_SCOPE);
    // The node the request names, not only the `node` object: a host that reads the node from
    // the URL (the plan view, D-7.33-K) holds no object for a pasted or Back/Forward link.
    if (nodeId !== null) onNavigate(null);
  };
  // Story 7.31 (ADO-1113): the rail's Clear and the chips' "Clear filters" also untick "On
  // Offer". The URL writer folds the second write into the first's history entry.
  const clearAll = () => {
    clearNarrowing();
    if (collectedOffers) setCollectedOffers(false);
  };
  // Story 8.1 (ADO-2010): one removable chip per scope part.
  const withoutScope = (key: keyof ProductScope): ProductScope => withoutScopePart(scope, key);
  const scopeChips: DrillChip[] = [
    ...(scope.hierarchyCode !== undefined
      ? [
          {
            label: scopeLabel ?? 'Selected categories',
            onRemove: () => onScopeChange?.(withoutScope('hierarchyCode')),
          },
        ]
      : []),
    ...(scope.lsnCategory !== undefined
      ? [
          {
            label: scope.lsnCategory,
            onRemove: () => onScopeChange?.(withoutScope('lsnCategory')),
          },
        ]
      : []),
    ...(scope.globalFavourite !== undefined
      ? [
          {
            label: scopeLabel ?? scope.globalFavourite,
            onRemove: () => onScopeChange?.(withoutScope('globalFavourite')),
          },
        ]
      : []),
    ...(appliedScope.cateringOnly === true
      ? [
          {
            label: 'Catering only',
            onRemove: () => onScopeChange?.(withoutScope('cateringOnly')),
          },
        ]
      : []),
    ...(collectedOffers ? [{ label: 'On Offer', onRemove: () => setCollectedOffers(false) }] : []),
  ];
  const setCateringOnly = (on: boolean) =>
    onScopeChange?.(on ? { ...scope, cateringOnly: true } : withoutScope('cateringOnly'));
  const removeChip = (key: FacetKey, value: string) =>
    applyFilters(removeCoupledFilterValue(latestFiltersRef.current, key, value));
  // Story 7.35 (ADO-2032): the node the labels/chip describe. In controlled mode a
  // deep link arrives with only the node id (no `node` object) until the listing
  // resolves, so prefer the resolved `listing.node`, falling back to the
  // optimistic `node` prop (a just-clicked selection shows its chip instantly).
  const activeNode = listing?.node ?? node;
  // Epic 7 demo polish (2026-07-17) — the drill selection renders as a leading
  // blue chip (prototype ActiveFilterChips: department/category chips). Removing
  // it returns to the root listing.
  const drillChip =
    activeNode !== null
      ? { label: formatCategoryName(activeNode.name), onRemove: () => onNavigate(null) }
      : undefined;

  const filterPanel = (
    <FilterPanel
      facets={facets}
      narrowings={railNarrowings}
      unavailableNarrowings={unavailableNarrowings}
      selected={activeFilters}
      onToggleValue={toggleValue}
      onClearAll={clearAll}
      hasActive={filtersActive || collectedOffers}
      departments={departments}
      categories={categories}
      currentDepartmentId={currentDepartmentId}
      currentCategoryId={currentCategory?.id ?? null}
      onNavigate={onNavigate}
      collectedOffers={{ on: collectedOffers, onChange: setCollectedOffers }}
    />
  );

  return (
    <section className={styles.listing} aria-label="Product listing">
      {/* Prototype layout: a pinned filters rail on the LEFT (CollapsibleFilterSidebar,
          desktop only — flush to the viewport edge below the header) and the listing
          content on the RIGHT (toolbar + chips + grid + pagination footer). */}
      <div className={styles.shell}>
        {/* Story 7.29 (ADO-2025, D-7.29-35): the favourites view has the rail too. */}
        <CollapsibleFilterSidebar open={showFilters} className={styles.sidebar} showAdPlaceholder>
          <div id="category-filter-panel">{filterPanel}</div>
        </CollapsibleFilterSidebar>

        <div className={styles.content}>
          {/* Toolbar (prototype "Listing options" row): Filters switch (desktop) /
              Filters drawer button (<1024) on the left · breadcrumb in the middle ·
              result count + grid/list view toggle on the right. */}
          <div className={styles.toolbar} role="group" aria-label="Listing options">
            <button
              type="button"
              className={styles.filtersToggle}
              aria-expanded={showFilters}
              aria-controls="category-filter-panel"
              onClick={toggleFilters}
            >
              Filters
              <span
                className={`${styles.filtersSwitch} ${showFilters ? styles.filtersSwitchOn : ''}`}
                aria-hidden
              >
                <span className={styles.filtersKnob} />
              </span>
            </button>
            <button
              type="button"
              className={styles.drawerButton}
              aria-haspopup="dialog"
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="sliders" size={12} />
              Filters
            </button>
            {toolbarAction}

            <div className={styles.toolbarCrumb}>
              {favouritesView ? (
                // Prototype favourites view: heading + "View all" link replace
                // the breadcrumb.
                <div className={styles.favouritesHeadingRow}>
                  <h2 className={styles.favouritesHeading}>Favourites</h2>
                  <button type="button" className={styles.favouritesViewAll} onClick={viewAll}>
                    View all
                  </button>
                </div>
              ) : (
                <Breadcrumb
                  trail={listing?.breadcrumb ?? (activeNode !== null ? [activeNode] : [])}
                  onNavigate={onNavigate}
                  leadingLinks={breadcrumbLeadingLinks}
                  rootLabel={breadcrumbRootLabel}
                />
              )}
            </div>

            <div className={styles.toolbarRight}>
              {listing !== null && (
                <p className={styles.count} role="status">
                  {
                    // Story 7-FU-C (ADO-1216): the current page's window into the
                    // full result set — "Showing 21–40 of 137 products". Anchored
                    // to the committed envelope (`rangeStart`/`rangeEnd`) so it
                    // can't invert or flash a stale range during navigation.
                    total === 0
                      ? 'No products'
                      : displayProducts.length === 0
                        ? // Empty window with total > 0 (the over-range data race) —
                          // report the count without a range rather than "0–20".
                          `${total} ${total === 1 ? 'product' : 'products'}`
                        : `Showing ${rangeStart}–${rangeEnd} of ${total} ${total === 1 ? 'product' : 'products'}`
                  }
                </p>
              )}
              <div className={styles.viewToggle} role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`${styles.viewButton} ${viewMode === 'grid' ? styles.viewButtonActive : ''}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  onClick={() => selectView('grid')}
                >
                  <Icon name="gridView" size={20} />
                </button>
                <button
                  type="button"
                  className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                  onClick={() => selectView('list')}
                >
                  <Icon name="listView" size={20} />
                </button>
              </div>
            </div>
          </div>

          {header}

          {/* Story 7.29 (ADO-2025, D-7.29-35): the favourites view shows its chips too. */}
          <FilterChips
            filters={appliedFilters}
            onRemove={removeChip}
            onClearAll={clearAll}
            drillChip={drillChip}
            scopeChips={scopeChips}
            className={styles.chipsRow}
          />
          {/* Story 8.1 (ADO-2010), A10.4: Catering only, offered to Foodservice only. Story 7.29
              (ADO-2025): and only where the listing offers it; disabled while no catering line is
              in scope and it is off (D-7.29-25). A Catering only scope from the URL shows the
              switch, on and enabled, before the first response, so it can be cleared. */}
          {offerCateringOnly &&
            (narrowings.includes('catering') ||
              (knownNarrowings === null && scope.cateringOnly === true)) &&
            onScopeChange !== undefined && (
              <div className={styles.cateringRow}>
                <Toggle
                  label="Catering only"
                  checked={appliedScope.cateringOnly === true}
                  disabled={
                    scope.cateringOnly !== true && unavailableNarrowings.includes('catering')
                  }
                  onChange={setCateringOnly}
                />
              </div>
            )}

          <div className={styles.main}>
            {favouritesView && phase === 'loading' && listing === null && (
              <p className={styles.status} role="status">
                Loading your favourites…
              </p>
            )}
            {favouritesView && (phase === 'error' || favouritesError) && (
              <Banner type="error">
                We couldn&rsquo;t load your favourites.{' '}
                <button
                  type="button"
                  className={styles.favouritesRetry}
                  onClick={() => {
                    if (favouritesError) void hydrateFavourites();
                    retryFetch();
                  }}
                >
                  Try again
                </button>
              </Banner>
            )}
            {/* The favourites empty states wait while a read is in flight, and while the page on
                screen still holds a favourite the list has dropped (a removal's re-read, from the
                commit the heart is removed in), so the heart prompt never flashes. */}
            {favouritesView &&
              !favouritesSettling &&
              listing !== null &&
              displayProducts.length === 0 &&
              total === 0 &&
              favouritesHydrated &&
              favourites.length > 0 &&
              !filtersActive &&
              !collectedOffers &&
              node === null && (
                // D-7.29-35: the list is range-scoped; the person's favourites are elsewhere.
                <div className={styles.empty} role="status">
                  <p className={styles.emptyTitle}>
                    Your favourites are in other ranges. Switch what you are ordering from to see
                    them.
                  </p>
                </div>
              )}
            {favouritesView &&
              !favouritesSettling &&
              (favouritesHydrated || favouritesError) &&
              listing !== null &&
              displayProducts.length === 0 &&
              !(total === 0 && favouritesHydrated && favourites.length > 0) &&
              !filtersActive &&
              !collectedOffers &&
              node === null && (
                // Prototype favourites empty state: heart circle + prompt + link.
                <div className={styles.empty} role="status">
                  <span className={styles.emptyHeart} aria-hidden="true">
                    <Icon name="heart" size={28} />
                  </span>
                  <p className={styles.emptyTitle}>Tap the heart on any product to save it here</p>
                  <button type="button" className={styles.favouritesViewAll} onClick={viewAll}>
                    Browse all products
                  </button>
                </div>
              )}
            {favouritesView &&
              !favouritesSettling &&
              listing !== null &&
              total === 0 &&
              (filtersActive || collectedOffers || node !== null) && (
                <div className={styles.empty} role="status">
                  <p className={styles.emptyTitle}>
                    None of your favourites match the selected filters.
                  </p>
                  <p className={styles.emptyHint}>Try removing a filter to widen your results.</p>
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                </div>
              )}

            {!favouritesView && phase === 'loading' && listing === null && (
              <p className={styles.status} role="status">
                Loading products…
              </p>
            )}

            {!favouritesView && phase === 'error' && (
              <Banner type="error">Couldn&rsquo;t load products. Please try again.</Banner>
            )}

            {!favouritesView &&
              collectedOffers &&
              listing?.unavailableOfferKinds?.includes('FEATURED_LINE') === true && (
                // Story 7.32 (ADO-1115): the Featured Lines could not be read this time (their
                // curation or their pricing failed). The other offers are listed; this is not the
                // AC2 "nothing featured" state, so the Shopper is told rather than shown nothing.
                <Banner type="warning">
                  Featured Lines can&rsquo;t be loaded right now. Everything else on offer is shown.
                </Banner>
              )}
            {phase === 'notFound' && (
              // Story 7.35 (ADO-2032), Decision 8 — a valid-but-missing category
              // UUID (a stale deep link): an explicit not-found state with a route
              // back to the catalogue root, never a silent fall-back to root or a
              // generic error.
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>We couldn&rsquo;t find that category.</p>
                <p className={styles.emptyHint}>It may have been moved or removed.</p>
                <Button variant="secondary" onClick={() => onNavigate(null)}>
                  {notFoundBackLabel}
                </Button>
              </div>
            )}

            {!favouritesView && listing !== null && total === 0 && !collectedOffers && (
              // AC6 / AC4 — explicit empty state, not an error. Shown when the node
              // (under the active filters) has NO products at all — `total === 0`,
              // so there are no pages to navigate to either. When attribute filters
              // are active, offer a clear-filters affordance to recover.
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>
                  {emptyCopy !== undefined && !filtersActive
                    ? emptyCopy.title
                    : filtersActive
                      ? `No products in ${activeNode !== null ? formatCategoryName(activeNode.name) : 'the catalogue'} match the selected filters.`
                      : `There are no products in ${activeNode !== null ? formatCategoryName(activeNode.name) : 'the catalogue'} yet.`}
                </p>
                <p className={styles.emptyHint}>
                  {emptyCopy !== undefined && !filtersActive
                    ? emptyCopy.hint
                    : filtersActive
                      ? 'Try removing a filter to widen your results.'
                      : 'Try another category, or check back once the range is updated.'}
                </p>
                {filtersActive && (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {!favouritesView && listing !== null && total === 0 && collectedOffers && (
              // Story 7.31 (ADO-1113), AC1/AC3 — the TRUTHFUL collected-offers empty state. A
              // successful read that found no live, scoped, priced and orderable offer is not an
              // error and not a fabricated "come back later": it tells the Shopper, plainly, that
              // nothing on their Range (or on the part of it they have narrowed to) is on offer
              // right now. A transport/server failure stays the error Banner above; this branch is
              // only reached on a settled `listing`. A drilled department or category narrows the
              // collection as much as a filter does, so it gets the narrowed copy and the Clear.
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>
                  {filtersActive || nodeId !== null
                    ? 'Nothing in this part of your range is on offer right now.'
                    : 'Nothing on your range is on offer right now.'}
                </p>
                <p className={styles.emptyHint}>
                  {filtersActive || nodeId !== null
                    ? 'Try removing a filter to see more of what is on offer.'
                    : 'Check back once this cycle’s offers are published.'}
                </p>
                {(filtersActive || nodeId !== null) && (
                  // Clears the narrowing within the collection; "On Offer" stays on.
                  <Button variant="secondary" onClick={clearNarrowing}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {overRange && (
              // Story 7-FU-C review (PR #252) — an empty window while `total > 0`.
              // Distinct from `total === 0` (a genuinely empty node) and from a load
              // error: the results changed under the current page, so recover to the
              // first page rather than leaving the grid, empty-state AND error all
              // unrendered (which used to leave just a bare footer).
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>That page is no longer available.</p>
                <p className={styles.emptyHint}>The results changed while you were browsing.</p>
                <Button variant="secondary" onClick={() => requestPage(1)}>
                  Back to the first page
                </Button>
              </div>
            )}

            {favouritesView
              ? // Story 17.1 (ADO-3722): the page's favourites under the name of each list that
                // holds them, a product on two lists under both. The cards are still the
                // favourites-scoped listing's (Story 7.29, ADO-2025), so the narrowings and the
                // pages apply. Read only: creating, renaming and moving between lists come in
                // Epic 24 (Stories 24.1 and 24.2).
                favouriteListSections(favouriteLists, displayProducts).map(
                  ({ list, products: listProducts, firstIndex }) => (
                    <section
                      key={list.id}
                      className={styles.favouritesList}
                      aria-labelledby={`favourites-list-${list.id}`}
                    >
                      <h3
                        id={`favourites-list-${list.id}`}
                        className={styles.favouritesListHeading}
                      >
                        {list.name}
                      </h3>
                      <ul className={viewMode === 'list' ? styles.list : styles.grid}>
                        {listProducts.map((product, index) => (
                          <li key={product.id}>
                            <ProductCard
                              product={product}
                              accountTypeEligible={
                                favouriteBySku.get(product.sku.toLowerCase())?.accountTypeEligible
                              }
                              priority={firstIndex + index < EAGER_IMAGE_COUNT}
                              layout={viewMode}
                              favouritesView
                            />
                          </li>
                        ))}
                      </ul>
                    </section>
                  ),
                )
              : displayProducts.length > 0 && (
                  <ul className={viewMode === 'list' ? styles.list : styles.grid}>
                    {displayProducts.map((product, index) => (
                      <li key={product.id}>
                        <ProductCard
                          product={product}
                          priority={index < EAGER_IMAGE_COUNT}
                          layout={viewMode}
                          favouritesView={false}
                        />
                      </li>
                    ))}
                  </ul>
                )}

            {/* Story 7-FU-C (ADO-1216), AC2 — the FUNCTIONAL pagination footer.
                "Per page" picks the page size (resets to page 1); "Previous"/"Next"
                step through the numbered pages (disabled at the ends and while a
                page is loading); "Page x of y" reads from the server `total`. A
                failed navigation keeps the grid + shows the retry notice below.
                Hidden while `overRange` (an empty window past the data) so the sole
                way out of a dead page is the "Back to the first page" recovery
                above, not a second one via a still-live pager (PR #252 review, r3). */}
            {listing !== null && total > 0 && !overRange && (
              <Pagination
                page={committedPage}
                totalPages={totalPages}
                pageSize={committedPageSize}
                pageSizeOptions={BROWSE_PAGE_SIZE_OPTIONS}
                onPageChange={(next) => (next < committedPage ? goPrev() : goNext())}
                onPageSizeChange={selectPageSize}
                busy={navigating}
                idPrefix="catalogue"
              />
            )}

            {pageError && (
              <p className={styles.loadMoreHint} role="status">
                Couldn&rsquo;t load that page.{' '}
                <button type="button" className={styles.favouritesRetry} onClick={retryFetch}>
                  Try again
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile/tablet (<1024px) filters drawer — the SAME panel, in the shared
          left drawer with a sticky "Apply filters" footer (filtering is already
          live; the button just closes the drawer, prototype parity). */}
      <FiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {filterPanel}
      </FiltersDrawer>
    </section>
  );
}

/**
 * The favourites lists with a product on the page, each with those products in the list's own
 * order and the position of its first card across the whole screen, so only the first few images
 * load eagerly (Story 17.1, ADO-3722). A product on two lists is shown under both.
 */
function favouriteListSections<P extends { readonly sku: string }>(
  lists: readonly FavouriteList[],
  pageProducts: readonly P[],
): Array<{
  readonly list: FavouriteList;
  readonly products: readonly P[];
  readonly firstIndex: number;
}> {
  const bySku = new Map(pageProducts.map((product) => [product.sku.toLowerCase(), product]));
  let firstIndex = 0;
  const sections: Array<{
    readonly list: FavouriteList;
    readonly products: readonly P[];
    readonly firstIndex: number;
  }> = [];
  for (const list of lists) {
    const products = list.favourites
      .map((favourite) => bySku.get(favourite.sku.toLowerCase()))
      .filter((product): product is P => product !== undefined);
    if (products.length === 0) continue;
    sections.push({ list, products, firstIndex });
    firstIndex += products.length;
  }
  return sections;
}
